<script setup>
import { onBeforeUnmount, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import ContactAPI from 'dashboard/api/contacts';
import Button from 'dashboard/components-next/button/Button.vue';
const route = useRoute();
const exports = ref([]);
const failed = ref(false);
const loading = ref(false);
const downloading = ref(null);
const downloadFailed = ref(false);
const statusNames = {
  pending: 'Na fila',
  processing: 'Gerando arquivo',
  completed: 'Pronto para baixar',
  failed: 'Falha na geração',
};
const fieldNames = {
  phone_number: 'Telefone',
  cpf: 'CPF',
  name: 'Nome',
  email: 'E-mail',
  id: 'ID',
  labels: 'Etiquetas',
};
let version = 0;
let timer;
const load = async () => {
  version += 1;
  const request = version;
  clearTimeout(timer);
  loading.value = true;
  try {
    const { data } = await ContactAPI.getExports();
    if (request !== version) return;
    exports.value = data.payload;
    failed.value = false;
  } catch {
    if (request === version) failed.value = true;
  } finally {
    if (request === version) {
      loading.value = false;
      timer = setTimeout(load, 5000);
    }
  }
};
watch(
  () => route.params.accountId,
  () => {
    exports.value = [];
    load();
  },
  { immediate: true }
);
onBeforeUnmount(() => {
  version += 1;
  clearTimeout(timer);
});
const download = async item => {
  const accountId = route.params.accountId;
  downloading.value = item.id;
  downloadFailed.value = false;
  try {
    const response = await ContactAPI.downloadExport(item.id);
    if (accountId !== route.params.accountId) return;
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = `contatos-${item.id}.csv`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch {
    downloadFailed.value = true;
  } finally {
    downloading.value = null;
  }
};
const date = value =>
  new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section
    class="flex h-full w-full flex-col gap-5 overflow-auto bg-n-background p-6"
  >
    <header class="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h1 class="m-0 text-xl font-semibold text-n-slate-12">
          Minhas exportações
        </h1>
        <p class="mb-0 mt-1 text-sm text-n-slate-11">
          Acompanhe suas últimas 50 solicitações e baixe os arquivos sem e-mail.
        </p>
      </div>
      <div class="flex items-center gap-2">
        <RouterLink
          :to="{ name: 'contacts_dashboard_index' }"
          class="text-sm text-n-brand"
          >Voltar aos contatos</RouterLink
        ><Button
          label="Atualizar"
          variant="outline"
          :is-loading="loading"
          @click="load"
        />
      </div>
    </header>
    <p
      v-if="failed || downloadFailed"
      role="alert"
      class="rounded-lg bg-n-ruby-3 p-3 text-sm text-n-ruby-11"
    >
      Não foi possível
      {{ downloadFailed ? 'baixar o arquivo' : 'atualizar a lista' }}. Tente
      novamente.
    </p>
    <p
      v-if="!loading && !exports.length && !failed"
      class="text-sm text-n-slate-11"
    >
      Nenhuma exportação solicitada. Nos contatos, escolha Exportar contatos
      para gerar seu primeiro arquivo.
    </p>
    <div
      v-for="item in exports"
      :key="item.id"
      class="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-n-weak bg-n-solid-1 p-4"
    >
      <div class="min-w-0 flex-1">
        <h2 class="m-0 text-sm font-semibold text-n-slate-12">
          {{ item.columns.map(key => fieldNames[key]).join(' · ') }}
        </h2>
        <p class="mb-0 mt-1 text-xs text-n-slate-11">
          {{ date(item.created_at) }} · {{ statusNames[item.status] }}
        </p>
        <p
          v-if="item.status === 'processing'"
          class="mb-0 mt-2 text-xs text-n-slate-11"
        >
          {{ item.processed_records }} de {{ item.total_records }} contatos
          processados
        </p>
        <progress
          v-if="item.status === 'processing'"
          :value="item.processed_records"
          :max="Math.max(item.total_records, 1)"
          aria-label="Contatos processados"
          class="mt-2 h-1.5 w-full [accent-color:currentColor]"
        />
        <p
          v-if="item.status === 'completed'"
          class="mb-0 mt-2 text-xs text-n-slate-11"
        >
          {{ item.total_records }} contatos no arquivo
        </p>
        <p v-if="item.error_message" class="mb-0 mt-2 text-sm text-n-ruby-11">
          {{ item.error_message }}
        </p>
      </div>
      <Button
        v-if="item.downloadable"
        label="Baixar CSV"
        icon="i-hugeicons-download-04"
        :is-loading="downloading === item.id"
        @click="download(item)"
      />
    </div>
  </section>
</template>
